import { Link, type LinkProps } from '@tanstack/react-router';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { EVENTS, track } from '../helpers/analytics';
import { appendUtm, getUtm, utmProps } from '../helpers/utm';
import { Tooltip } from './popover/tooltip';

const appUrl = import.meta.env.VITE_APP_URL;

type ReactButton = React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>>;
type ReactLinkButton = React.FC<LinkProps>;

type ReasonedButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /**
   * Why the button can't be used right now. When set, the button renders
   * disabled and the reason opens as a tooltip on hover or keyboard focus.
   * A control that greys out without a word, or vanishes, leaves the user
   * guessing; the role-gated order actions all needed the same answer, so it
   * lives on the button rather than at each call site.
   */
  disabledReason?: string | null;
};
type ReasonedButton = React.FC<ReasonedButtonProps>;

/**
 * The one place a `disabledReason` is turned into a disabled button plus a
 * tooltip; the styled variants below only supply their classes. A disabled
 * button swallows pointer events and can't take focus, so the tooltip's
 * trigger is a focusable span around it rather than the button itself.
 * Without a reason the button renders bare, with no wrapper.
 */
const BaseButton: ReasonedButton = ({ disabled, disabledReason, ...properties }) => {
  const button = <button disabled={disabled || Boolean(disabledReason)} {...properties} />;
  if (!disabledReason) {
    return button;
  }

  return (
    <Tooltip content={disabledReason} side="bottom">
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: the span stands in for the disabled button as the tooltip trigger, so the reason stays reachable by keyboard */}
      <span className="inline-flex" tabIndex={0}>
        {button}
      </span>
    </Tooltip>
  );
};

// Solid-color buttons (PrimaryButton, DangerButton, PrimaryLinkButton) get
// their focus indicator from an inset 1px ring rather than the global
// catchall outline: an external outline at 1px offset paints in the gap
// between buttons and reads as a separate halo; inset stays on-button and
// doesn't change the apparent footprint. The ring color is a pale tint of
// the button's own fill — primary-200 (pale mint) inside the emerald fill,
// red-200 (pale pink) inside the red fill. White read as alien against
// either saturated color; a paler tint of the same hue stays in the brand
// family and feels like a halo coming *from* the button. Picked -200 over
// -300 on iteration so the halo reads as ambient light, not a second
// stripe of the brand color.
const solidButtonFocusBaseClass =
  'focus:outline-none focus-visible:ring-1 focus-visible:ring-inset';
const primarySolidFocusClass = `${solidButtonFocusBaseClass} focus-visible:ring-primary-200`;
const dangerSolidFocusClass = `${solidButtonFocusBaseClass} focus-visible:ring-red-200`;

// Solid-fill buttons mirror the secondaries' shape grammar (1px contour +
// lift) so the toolbar reads as one family after the v6 contrast pass, but
// each piece is tuned to saturated fills:
//   • Lift: shadow-toolbar-solid (24%/14% black, two layers) — secondaries'
//     shadow-toolbar at 10% disappears against emerald/red; see the token
//     comment in index.css for the rationale.
//   • Contour: --color-button-edge-primary on emerald, --color-button-edge-
//     danger on red. Barely visible on a good monitor, defined under a
//     contrast clamp; on hover the primary fill consolidates to primary-600
//     (same as the ring) so the ring melts into the surface and the press
//     state reads as a single deeper plate.
// Disabled drops both shadow and ring so an off button doesn't look
// pressable, and the ring goes gray-300 to disappear into the gray fill.
// Disabled state mirrors IconButton's quiet treatment: a near-white fill
// (gray-50) with grayed text (gray-400) and a dim ring (gray-300). The earlier
// loud-gray-400 fill read as a colored button-that-doesn't-respond; the new
// treatment reads unambiguously as "not available." Shadow drops so the button
// stops looking liftable.
const solidButtonDisabledClass =
  'disabled:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:shadow-none disabled:cursor-not-allowed';
const solidButtonChromeClass = 'shadow-toolbar-solid ring-1 ring-inset';

export const PrimaryButtonClassname = `h-10 py-2 px-4 bg-primary-500 text-white rounded-sm ${solidButtonChromeClass} ring-button-edge-primary hover:bg-primary-600 transition duration-150 cursor-pointer flex items-center justify-center gap-2 ${solidButtonDisabledClass} ${primarySolidFocusClass}`;

export const PrimaryButton: ReasonedButton = ({ className, ...properties }) => (
  <BaseButton className={`${PrimaryButtonClassname} ${className ?? ''}`} {...properties} />
);

// Toolbar-edge ring (gray-400, one shade darker than form-input gray-300)
// plus shadow-toolbar (= shadow-sm) rather than shadow-xs: secondary buttons
// live on the white toolbar, where a gray-300 ring + minimal shadow collapses
// into the background under a contrast clamp or to older eyes. The darker
// ring + slightly fuller lift gives the button a defined "clickable surface"
// identity without crossing into solid-fill territory. Form-input rings stay
// at gray-300 — modals get the user's full attention; the toolbar shares it
// with the table. `items-center` so leading icons (toolbar funnel, CSV arrow)
// and trailing chevrons sit on the label's baseline. Disabled state matches
// the quiet pattern used elsewhere (gray-50 fill, gray-400 text, gray-300
// ring) AND drops the shadow so the button stops looking liftable — same
// treatment the solid family gets via `solidButtonDisabledClass`. Without
// this, a disabled Reset Filters sits a hair higher than a disabled Purchase
// Order next to it.
export const SecondaryButtonClassname =
  'py-2 px-4 bg-white text-gray-900 shadow-toolbar ring-1 ring-inset ring-toolbar-edge rounded-sm hover:bg-gray-50 transition duration-150 flex items-center gap-2 disabled:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:shadow-none cursor-pointer disabled:cursor-not-allowed focus:outline-none focus-visible:ring-toolbar-edge-focus';

export const SecondaryButton: ReasonedButton = (properties) => (
  <BaseButton type="button" className={SecondaryButtonClassname} {...properties} />
);

const iconButtonClass =
  'inline-flex shrink-0 items-center justify-center h-10 px-4 rounded-sm bg-white text-icon shadow-toolbar ring-1 ring-inset ring-toolbar-edge hover:bg-gray-50 transition duration-150 disabled:bg-gray-50 disabled:text-gray-400 disabled:shadow-none disabled:cursor-not-allowed cursor-pointer focus-visible:outline-none focus-visible:ring-toolbar-edge-focus';

type IconButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  tooltip: string;
  children: ReactNode;
};

/**
 * Quiet white icon button used in toolbars and modals (CSV, Print, etc.).
 * Pair with a 20px heroicon. The tooltip text doubles as the accessible name.
 */
export const IconButton = ({
  tooltip,
  className,
  'aria-label': ariaLabel,
  ...rest
}: IconButtonProps) => (
  <Tooltip content={tooltip} side="bottom">
    <button
      type="button"
      aria-label={ariaLabel ?? tooltip}
      className={className ? `${iconButtonClass} ${className}` : iconButtonClass}
      {...rest}
    />
  </Tooltip>
);

const smallOutlineDisabled =
  'disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent';

// Small-outline family — SmallSecondary / SmallEmerald / SmallDanger share
// one geometry (py-0.5, font-medium, rounded, 1px border, no shadow) so
// they read as one vocabulary in inline table affordances and confirm-row
// pairs. The only thing that varies across the three is the color triplet
// (border / text / hover-fill). `justify-center` is a no-op at the family's
// usual content width and only bites where a caller stacks two of them in a
// flex column — the stretched buttons then match width and center their
// labels instead of hugging the left edge.
const smallOutlineGeometry =
  'inline-flex items-center justify-center gap-1 rounded border px-2 py-0.5 text-xs font-medium cursor-pointer';

const smallSecondaryClass = `${smallOutlineGeometry} border-toolbar-edge bg-white text-gray-900 hover:bg-gray-50 ${smallOutlineDisabled}`;

export const SmallSecondaryButton: ReactButton = ({ className, ...properties }) => (
  <button type="button" className={`${smallSecondaryClass} ${className ?? ''}`} {...properties} />
);

// Link twin of SmallSecondaryButton, for a small affordance that navigates
// rather than acts. Same class string, so the two stay one control in the
// eye even when a row pairs them (Edit + Cancel on an order).
export const SmallSecondaryLinkButton: ReactLinkButton = (properties) => (
  <Link className={smallSecondaryClass} {...properties} />
);

export const SmallEmeraldButton: ReactButton = ({ className, ...properties }) => (
  <button
    type="button"
    className={`${smallOutlineGeometry} border-emerald-500 text-emerald-700 hover:bg-emerald-50 ${smallOutlineDisabled} ${className ?? ''}`}
    {...properties}
  />
);

// Routed through BaseButton rather than a bare <button> like its two
// siblings: this is the small variant of the role-gated order actions, so it
// needs the same `disabledReason` tooltip DangerButton has. The geometry and
// colors are unchanged.
export const SmallDangerButton: ReasonedButton = ({ className, ...properties }) => (
  <BaseButton
    type="button"
    className={`${smallOutlineGeometry} border-red-300 text-red-700 hover:bg-red-50 ${smallOutlineDisabled} ${className ?? ''}`}
    {...properties}
  />
);

export const DangerButton: ReasonedButton = (properties) => (
  <BaseButton
    className={`py-2 px-4 bg-red-500 text-white rounded-sm ${solidButtonChromeClass} ring-button-edge-danger hover:bg-red-700 transition duration-150 cursor-pointer ${solidButtonDisabledClass} ${dangerSolidFocusClass}`}
    {...properties}
  />
);

// Shares PrimaryButtonClassname so a link-styled primary action is pixel-identical
// to PrimaryButton — same 40px height, centering, solid-button lift, and focus
// ring. (Previously this diverged and rendered a few px shorter than PrimaryButton.)
export const PrimaryLinkButton: ReactLinkButton = (properties) => (
  <Link className={PrimaryButtonClassname} {...properties} />
);

export const SecondaryLinkButton: ReactLinkButton = (properties) => (
  <Link
    className="flex items-center gap-2 py-2 px-4 bg-white text-gray-900 shadow-toolbar ring-1 ring-inset ring-toolbar-edge rounded-sm hover:bg-gray-50 transition duration-150 disabled:bg-gray-50 disabled:text-gray-400 disabled:ring-gray-300 disabled:shadow-none disabled:cursor-not-allowed cursor-pointer focus:outline-none focus-visible:ring-toolbar-edge-focus"
    {...properties}
  />
);

export const LinkButton: ReactLinkButton = (properties) => (
  <Link className={SecondaryButtonClassname} {...properties} />
);

type AnchorProperties = React.DetailedHTMLProps<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

export const ExternalLinkButton: React.FC<AnchorProperties> = (properties) => (
  <a
    className="py-2 px-4 bg-gray-500 text-white rounded-sm hover:bg-gray-700 transition duration-150 cursor-pointer"
    {...properties}
  />
);

export const LoginButton: React.FC<AnchorProperties> = ({ className, children, ...properties }) => (
  <a
    href={appUrl}
    className={
      className ??
      'py-2 px-4 text-white hover:bg-primary-800 rounded-md font-medium transition-all duration-200'
    }
    {...properties}
  >
    {children ?? 'Log In'}
  </a>
);

export const SignupButton: React.FC<AnchorProperties> = ({
  className,
  children,
  href,
  onClick,
  ...properties
}) => {
  const utm = getUtm();
  const decoratedHref = appendUtm(href ?? `${appUrl}/signup`, utm);

  return (
    <a
      href={decoratedHref}
      onClick={(event) => {
        track(EVENTS.signupLinkClick, utmProps(utm));
        onClick?.(event);
      }}
      className={
        className ??
        'py-2 px-4 text-white hover:bg-primary-800 rounded-md font-medium transition-all duration-200'
      }
      {...properties}
    >
      {children ?? 'Sign Up'}
    </a>
  );
};
